import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import axios from "axios";
import AddIcon from "@mui/icons-material/Add";
import MaterialTable from "../../components/Table/MaterialTable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { BASE_URL } from "../../utils/constants";

const RoomTypes = () => {
  // Add state for grid view and form dialog
  const [showForm, setShowForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const [roomTypeData, setRoomTypeData] = useState({
    hotel_id: "",
    room_type: "",
    room_type_description: "",
    number_of_rooms: "",
    customer_rating: "",
    is_popularbooking: "0",
    is_active: "1",
    no_of_adults: "",
    no_of_children: "",
  });

  const [hotels, setHotels] = useState([]);
  const [images, setImages] = useState([]);
  const [viewImages, setViewImages] = useState([]);
  const [roomImagesData, setRoomImagesData] = useState({
    is_active: "1",
  });
  const [roomTypes, setRoomTypes] = useState([]);
  const [isCustomRoomType, setIsCustomRoomType] = useState(false);
  const [existingImages, setExistingImages] = useState([]);
  const [existingViewImages, setExistingViewImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);

  const handleExportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(roomTypes);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "RoomTypes");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, `RoomTypes_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  const columns = [
    { title: "Hotel ID", field: "hotel_id" },
    { title: "Room Type", field: "room_type" },
    { title: "Description", field: "room_type_description" },
    { title: "No. of Rooms", field: "number_of_rooms", type: "numeric" },
    { title: "Customer Rating", field: "customer_rating", type: "numeric" },
    { title: "Popular Booking", field: "is_popularbooking" },
    { title: "Active", field: "is_active" },
    { title: "Adults", field: "no_of_adult", type: "numeric" },
    { title: "Children", field: "no_of_children", type: "numeric" },
  ];

  // Add the room types fetch effect
  useEffect(() => {
    const fetchRoomTypes = async () => {
      try {
        const response = await axios.get(`${BASE_URL}HotelRoom`);
        if (response.data && Array.isArray(response.data.data)) {
          setRoomTypes(response.data.data);
        }
      } catch (error) {
        console.error("Failed to fetch room types:", error);
      }
    };

    fetchRoomTypes();
  }, []);

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);

    const validatedImages = await Promise.all(
      files.map((file) => {
        return new Promise((resolve) => {
          const img = new Image();
          const objectUrl = URL.createObjectURL(file);
          img.onload = () => {
            const isValidSize = file.size / 1024 / 1024 <= 5; // ≤ 5MB
            const isValidDimensions = img.width <= 392 && img.height <= 300; // ≤ 392x300

            if (!isValidSize) {
              alert(
                `"${file.name}" exceeds 5MB. Please upload a smaller image.`,
              );
              resolve(null);
            } else if (!isValidDimensions) {
              alert(
                `"${file.name}" must be equal to or smaller than 392x300 pixels (current: ${img.width}x${img.height}).`,
              );
              resolve(null);
            } else {
              resolve({
                file,
                preview: objectUrl,
                seq_no: "",
              });
            }
          };
          img.onerror = () => {
            alert(`"${file.name}" could not be validated. Try again.`);
            resolve(null);
          };
          img.src = objectUrl;
        });
      }),
    );

    const validImages = validatedImages.filter(Boolean);
    setImages((prev) => [...prev, ...validImages]);
  };

  const handleSequenceChange = (index, value) => {
    const updatedImages = [...images];
    updatedImages[index].seq_no = value;
    updatedImages[index].sequenceChanged = true;
    setImages(updatedImages);
  };

  const handleRemoveImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const handleViewImageChange = async (e) => {
    const files = Array.from(e.target.files);

    const validatedImages = await Promise.all(
      files.map((file) => {
        return new Promise((resolve) => {
          const img = new Image();
          const objectUrl = URL.createObjectURL(file);
          img.onload = () => {
            const isValidSize = file.size / 1024 / 1024 <= 5; // ≤ 5MB
            const isValidDimensions = img.width <= 392 && img.height <= 300; // ≤ 392x300

            if (!isValidSize) {
              alert(
                `"${file.name}" exceeds 5MB. Please upload a smaller image.`,
              );
              resolve(null);
            } else if (!isValidDimensions) {
              alert(
                `"${file.name}" must be equal to or smaller than 392x300 pixels (current: ${img.width}x${img.height}).`,
              );
              resolve(null);
            } else {
              resolve({
                file,
                preview: objectUrl,
                seq_no: "",
              });
            }
          };
          img.onerror = () => {
            alert(`"${file.name}" could not be validated. Try again.`);
            resolve(null);
          };
          img.src = objectUrl;
        });
      }),
    );

    const validImages = validatedImages.filter(Boolean);
    setViewImages((prev) => [...prev, ...validImages]);
  };

  const handleViewSequenceChange = (index, value) => {
    const updatedImages = [...viewImages];
    updatedImages[index].seq_no = value;
    updatedImages[index].sequenceChanged = true;
    setViewImages(updatedImages);
  };

  const handleViewRemoveImage = (index) => {
    setViewImages(viewImages.filter((_, i) => i !== index));
  };

  // Add functions to handle create and edit actions
  const handleCreateNew = () => {
    setRoomTypeData({
      hotel_id: "",
      room_type: "",
      room_type_description: "",
      number_of_rooms: "",
      customer_rating: "",
      is_popularbooking: "0",
      is_active: "1",
      no_of_adults: "",
      no_of_children: "",
    });
    setImages([]);
    setViewImages([]);
    setIsCustomRoomType(true);
    setIsEditMode(false);
    setShowForm(true);
  };

  const handleEditRoomType = (roomType) => {
    // Set loading state while fetching data
    setLoading(true);

    // Set basic room type data from the table row
    setRoomTypeData({
      hotel_id: roomType.hotel_id.toString(),
      room_type: roomType.room_type,
      room_type_description: roomType.room_type_description || "",
      number_of_rooms: roomType.number_of_rooms?.toString() || "",
      customer_rating: roomType.customer_rating?.toString() || "",
      is_popularbooking: roomType.is_popularbooking?.toString() || "0",
      is_active: roomType.is_active ? "1" : "0",
      no_of_adults: roomType.no_of_adult?.toString() || "",
      no_of_children: roomType.no_of_children?.toString() || "",
    });

    setIsCustomRoomType(false);
    setIsEditMode(true);
    setShowForm(true);

    // Fetch room images and view images
    fetchRoomTypeImages(roomType.hotel_id, roomType.room_type).finally(() => {
      setLoading(false);
    });
  };

  const fetchRoomTypeImages = async (hotel_id, room_type) => {
    try {
      const response = await axios.post(
        `${BASE_URL}get_api/room_type/data_record`,
        {
          hotel_id: parseInt(hotel_id),
          room_type: room_type,
        },
      );

      const data = Array.isArray(response.data)
        ? response.data[0]
        : response.data;

      if (data) {
        if (data.images?.length) {
          const formattedImages = data.images.map((img) => ({
            preview: img.image_path,
            seq_no: img.image_seq_no?.toString() || "",
            image_id: img.image_id,
          }));
          setImages(formattedImages);
        } else {
          setImages([]);
        }

        if (data.view_images?.length) {
          const formattedViewImages = data.view_images.map((img) => ({
            preview: img.view_image_path,
            seq_no: img.view_image_seq_no?.toString() || "",
            image_id: img.image_id,
          }));
          setViewImages(formattedViewImages);
        } else {
          setViewImages([]);
        }
      }
    } catch (error) {
      console.error("Failed to fetch room type images:", error);
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
  };

  const handleRoomTypeSubmit = async () => {
    if (
      !roomTypeData.hotel_id ||
      !roomTypeData.room_type ||
      !roomTypeData.room_type_description
    ) {
      alert("Please fill in all required fields");
      return;
    }

    try {
      const payload = {
        data: {
          hotel_id: parseInt(roomTypeData.hotel_id),
          room_type: roomTypeData.room_type,
          room_type_description: roomTypeData.room_type_description,
          number_of_rooms: parseInt(roomTypeData.number_of_rooms),
          customer_rating: parseFloat(roomTypeData.customer_rating),
          is_popularbooking: parseInt(roomTypeData.is_popularbooking),
          is_active: parseInt(roomTypeData.is_active) === 1,
          no_of_adults: parseInt(roomTypeData.no_of_adults),
          no_of_children: parseInt(roomTypeData.no_of_children),
        },
      };

      let response;
      let selectedRoomId;

      if (isCustomRoomType) {
        // Create new room type
        response = await axios.post(
          `${BASE_URL}create_room/api_hotel`,
          payload,
        );
        selectedRoomId = response.data.room_id;
      } else {
        // Update existing room type
        const selectedRoom = roomTypes.find(
          (room) =>
            room.hotel_id === parseInt(roomTypeData.hotel_id) &&
            room.room_type === roomTypeData.room_type,
        );
        payload.data.room_id = selectedRoom.room_id;
        response = await axios.put(
          `${BASE_URL}api/upadte_room/hotel_data`,
          payload,
        );
        selectedRoomId = selectedRoom.room_id;
      }

      console.log("Room operation successful:", response.data);
      return selectedRoomId;
    } catch (error) {
      console.error("Operation failed:", error);
      alert(
        "Operation failed: " + (error.response?.data?.message || error.message),
      );
      throw error;
    }
  };

  const handleImageUpload = async (room_id) => {
    if (images.some((img) => !img.seq_no)) {
      alert("Please provide sequence numbers for all images");
      return;
    }

    const formData = new FormData();

    // Only include new or modified images in the payload
    const imageDataArray = images
      .filter(
        (img) => img.file || (img.preview && !img.preview.startsWith("http")),
      ) // Only new or modified images
      .map((img) => ({
        hotel_id: parseInt(roomTypeData.hotel_id),
        room_id: room_id,
        is_active: roomImagesData.is_active,
        image_seq_no: parseInt(img.seq_no),
      }));

    // Don't proceed if no new or modified images
    if (imageDataArray.length === 0) {
      return;
    }

    formData.append("imageDataArray", JSON.stringify(imageDataArray));

    // Only append new files
    images.forEach((img) => {
      if (img.file) {
        formData.append("images", img.file);
      }
    });

    try {
      const url = isCustomRoomType
        ? `${BASE_URL}upload-room-images`
        : `${BASE_URL}api/update_room_images/data`;

      const method = isCustomRoomType ? "post" : "put";

      const response = await axios({
        method: method,
        url: url,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });

      console.log("Upload success:", response.data);
    } catch (error) {
      console.error("Upload failed:", error);
      throw error;
    }
  };

  const handleViewImageUpload = async (room_id) => {
    if (viewImages.some((img) => !img.seq_no)) {
      alert("Please provide sequence numbers for all images");
      return;
    }

    const formData = new FormData();

    // Only include new or modified images in the payload
    const imageDataArray = viewImages
      .filter(
        (img) => img.file || (img.preview && !img.preview.startsWith("http")),
      ) // Only new or modified images
      .map((img) => ({
        hotel_id: parseInt(roomTypeData.hotel_id),
        room_id: room_id,
        image_seq_no: parseInt(img.seq_no),
      }));

    // Don't proceed if no new or modified images
    if (imageDataArray.length === 0) {
      return;
    }

    formData.append("imageDataArray", JSON.stringify(imageDataArray));

    // Only append actual files
    viewImages.forEach((img) => {
      if (img.file) {
        formData.append("images", img.file);
      }
    });

    try {
      const url = isCustomRoomType
        ? `${BASE_URL}upload-hotel-room-images-view`
        : `${BASE_URL}api/updated-hotel-room-images-view/data`;

      const method = isCustomRoomType ? "post" : "put";

      const response = await axios({
        method: method,
        url: url,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });

      console.log("View images upload success:", response.data);
    } catch (error) {
      console.error("View images upload failed:", error);
      throw error;
    }
  };

  // Update handleCombinedSave to refresh the room types list and close the form
  const handleCombinedSave = async () => {
    try {
      const room_id = await handleRoomTypeSubmit();

      // Only upload if there are new images or sequence changes
      const hasImageChanges = images.some(
        (img) => img.file || img.sequenceChanged,
      );
      if (hasImageChanges) {
        await handleImageUpload(room_id);
      }

      const hasViewImageChanges = viewImages.some(
        (img) => img.file || img.sequenceChanged,
      );
      if (hasViewImageChanges) {
        await handleViewImageUpload(room_id);
      }

      // Refresh the room types list
      const response = await axios.get(`${BASE_URL}HotelRoom`);
      if (response.data && Array.isArray(response.data.data)) {
        setRoomTypes(response.data.data);
      }

      // Reset form and close dialog
      setRoomTypeData({
        hotel_id: "",
        room_type: "",
        room_type_description: "",
        number_of_rooms: "",
        customer_rating: "",
        is_popularbooking: "0",
        is_active: "1",
        no_of_adults: "",
        no_of_children: "",
      });
      setImages([]);
      setViewImages([]);
      setShowForm(false);

      alert("Room type saved successfully!");
    } catch (error) {
      console.error("Error saving data:", error);
      alert("Failed to save all data. Please try again.");
    }
  };

  useEffect(() => {
    const fetchHotels = async () => {
      try {
        const response = await axios.get(`${BASE_URL}Hotel`);
        if (response.data && Array.isArray(response.data.data)) {
          setHotels(response.data.data);
        }
      } catch (error) {
        console.error("Failed to fetch hotels:", error);
      }
    };

    fetchHotels();
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 4 }}>
        Room Types Management
      </Typography>
      Room Types Grid
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h5">Room Types</Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <MaterialTable
            title="Room Types"
            columns={columns}
            data={roomTypes}
            selection={false}
            actions={false}
            onEdit={(rowData) => handleEditRoomType(rowData)}
            toolbar={{ create: true, onCreateClick: handleCreateNew }}
            sx={{
              "& .MuiPaper-root": { width: "100%", overflow: "hidden" },
              "& .MuiTableContainer-root": {
                maxHeight: "calc(100vh - 250px)",
                overflowX: "auto",
                overflowY: "auto",
              },
            }}
          />
        )}
      </Box>
      {/* Room Type Form Dialog */}
      <Dialog open={showForm} onClose={handleCloseForm} maxWidth="lg" fullWidth>
        <DialogTitle>
          {isCustomRoomType ? "Create New Room Type" : "Edit Room Type"}
        </DialogTitle>
        <DialogContent>
          {/* Room Type Form */}
          <Box sx={{ mb: 4, mt: 2 }}>
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: "repeat(2, 1fr)",
              }}
            >
              <TextField
                select
                label="Hotel Name"
                value={roomTypeData.hotel_id}
                onChange={(e) =>
                  setRoomTypeData({ ...roomTypeData, hotel_id: e.target.value })
                }
                SelectProps={{
                  native: true,
                }}
                variant="outlined"
                fullWidth
                size="small"
                disabled={isEditMode}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "40px",
                    "& fieldset": {
                      borderColor: "#E0E0E0",
                    },
                    "&:hover fieldset": {
                      borderColor: "#BDBDBD",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1976D2",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    backgroundColor: "white",
                    padding: "0 8px",
                    marginLeft: "-4px",
                  },
                  "& .MuiSelect-select": {
                    padding: "8px 14px",
                  },
                }}
              >
                <option value="" disabled>
                  Select Hotel
                </option>
                {hotels
                  .filter((hotel) => hotel.is_active === true)
                  .map((hotel) => (
                    <option key={hotel.id} value={hotel.hotel_id}>
                      {hotel.name}
                    </option>
                  ))}
              </TextField>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {isCustomRoomType ? (
                  <TextField
                    label="Room Type"
                    value={roomTypeData.room_type}
                    onChange={(e) => {
                      // Allow only letters, numbers, spaces and hyphens
                      const formattedValue = e.target.value.replace(
                        /[^a-zA-Z0-9\s-]/g,
                        "",
                      );
                      // Convert to title case (capitalize first letter of each word)
                      const titleCaseValue = formattedValue
                        .split(" ")
                        .map(
                          (word) =>
                            word.charAt(0).toUpperCase() +
                            word.slice(1).toLowerCase(),
                        )
                        .join(" ");
                      setRoomTypeData({
                        ...roomTypeData,
                        room_type: titleCaseValue,
                      });
                    }}
                    fullWidth
                    size="small"
                    placeholder="Enter Room Type (e.g. Deluxe Room)"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "40px",
                        "& fieldset": {
                          borderColor: "#E0E0E0",
                        },
                        "&:hover fieldset": {
                          borderColor: "#BDBDBD",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#1976D2",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        backgroundColor: "white",
                        padding: "0 8px",
                        marginLeft: "-4px",
                      },
                    }}
                  />
                ) : (
                  <TextField
                    label="Room Type"
                    value={roomTypeData.room_type}
                    disabled
                    fullWidth
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "40px",
                      },
                    }}
                  />
                )}
              </Box>

              <TextField
                label="Number of Rooms"
                type="number"
                value={roomTypeData.number_of_rooms}
                onChange={(e) =>
                  setRoomTypeData({
                    ...roomTypeData,
                    number_of_rooms: e.target.value,
                  })
                }
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { height: "40px" } }}
              />

              <TextField
                label="Customer Rating"
                type="number"
                inputProps={{ min: 0, max: 5, step: 0.1 }}
                value={roomTypeData.customer_rating}
                onChange={(e) =>
                  setRoomTypeData({
                    ...roomTypeData,
                    customer_rating: e.target.value,
                  })
                }
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { height: "40px" } }}
              />

              <TextField
                label="Popular Booking"
                select
                value={roomTypeData.is_popularbooking}
                onChange={(e) =>
                  setRoomTypeData({
                    ...roomTypeData,
                    is_popularbooking: e.target.value,
                  })
                }
                SelectProps={{ native: true }}
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { height: "40px" } }}
              >
                <option value="0">No</option>
                <option value="1">Yes</option>
              </TextField>

              <TextField
                label="Active Status"
                select
                value={roomTypeData.is_active}
                onChange={(e) =>
                  setRoomTypeData({
                    ...roomTypeData,
                    is_active: e.target.value,
                  })
                }
                SelectProps={{ native: true }}
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { height: "40px" } }}
              >
                <option value="0">Inactive</option>
                <option value="1">Active</option>
              </TextField>

              <TextField
                label="Number of Adults"
                type="number"
                value={roomTypeData.no_of_adults}
                onChange={(e) =>
                  setRoomTypeData({
                    ...roomTypeData,
                    no_of_adults: e.target.value,
                  })
                }
                inputProps={{ min: 0 }}
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { height: "40px" } }}
              />

              <TextField
                label="Number of Children"
                type="number"
                value={roomTypeData.no_of_children}
                onChange={(e) =>
                  setRoomTypeData({
                    ...roomTypeData,
                    no_of_children: e.target.value,
                  })
                }
                inputProps={{ min: 0 }}
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { height: "40px" } }}
              />

              <TextField
                label="Description"
                multiline
                rows={2}
                value={roomTypeData.room_type_description}
                onChange={(e) =>
                  setRoomTypeData({
                    ...roomTypeData,
                    room_type_description: e.target.value,
                  })
                }
                sx={{ gridColumn: "1 / -1" }}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Room Images Section */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Hotel Room Images
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageChange}
                />
              </Grid>
            </Grid>

            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Preview</TableCell>
                    <TableCell>Sequence No</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {images.map((img, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <img
                          src={img.preview}
                          alt={`Preview ${index}`}
                          style={{
                            width: "100px",
                            height: "80px",
                            objectFit: "cover",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={img.seq_no}
                          onChange={(e) =>
                            handleSequenceChange(index, e.target.value)
                          }
                          sx={{ width: "100px" }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => handleRemoveImage(index)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Hotel Room Images View Section */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Hotel Room Images View
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleViewImageChange}
                />
              </Grid>
            </Grid>

            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Preview</TableCell>
                    <TableCell>Sequence No</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {viewImages.map((img, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <img
                          src={img.preview}
                          alt={`Preview ${index}`}
                          style={{
                            width: "100px",
                            height: "80px",
                            objectFit: "cover",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={img.seq_no}
                          onChange={(e) =>
                            handleViewSequenceChange(index, e.target.value)
                          }
                          sx={{ width: "100px" }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => handleViewRemoveImage(index)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForm}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCombinedSave}
            disabled={
              !roomTypeData.hotel_id ||
              !roomTypeData.room_type ||
              !roomTypeData.room_type_description
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoomTypes;

// Add this function inside the component, before the return statement
// const handleDeleteRoomType = (roomType) => {
//   if (window.confirm(`Are you sure you want to delete the room type "${roomType.room_type}"?`)) {
//     setLoading(true);
//
//     axios.delete(`${BASE_URL}api/delete_room/${roomType.room_id}`)
//       .then(response => {
//         // Refresh the room types list
//         const updatedRoomTypes = roomTypes.filter(room => room.room_id !== roomType.room_id);
//         setRoomTypes(updatedRoomTypes);
//         alert('Room type deleted successfully!');
//       })
//       .catch(error => {
//         console.error('Failed to delete room type:', error);
//         alert('Failed to delete room type. Please try again.');
//       })
//       .finally(() => {
//         setLoading(false);
//       });
//   }
// };
